import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lightning, Lock, Fire, Barbell } from "@phosphor-icons/react";
import ClassCard from "../components/ClassCard";
import ReelsRow from "../components/ReelsRow";
import { useEffect, useState } from "react";
import { api } from "../lib/api";

export default function Landing() {
  const [classes, setClasses] = useState([]);
  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_fit-class-hub-2/artifacts/e4rdb6e0_IMG_2218.jpeg"
            alt="Lucy training"
            className="w-full h-full object-cover object-[60%_center] opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1F1812] via-[#1F1812]/80 to-[#1F1812]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F1812] via-[#1F1812]/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-32 lg:py-44">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
          >
            <span className="label-overline text-[#D4A574]">// @TrainWithLucy Fitness</span>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl tracking-widest leading-none mt-6 max-w-3xl">
              Train with me.<br />Anywhere. Anytime.
            </h1>
            <p className="mt-8 max-w-xl text-base text-zinc-300 leading-relaxed">
              On-demand workout classes from Lucy — strength, conditioning, mobility and recovery. Built around you, streamed straight to your living room or gym.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/classes" className="kinetic-btn inline-flex items-center gap-2" data-testid="hero-explore-btn">
                Explore Classes <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/subscribe" className="label-overline text-white hover:text-[#D4A574] border border-white/20 px-6 py-4" data-testid="hero-subscribe-btn">
                Get All-Access £14.99/mo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Meet Lucy */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14 items-center" data-testid="meet-lucy-section">
        <div className="relative">
          <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
            <img
              src="https://customer-assets.emergentagent.com/job_fit-class-hub-2/artifacts/2cxy7uc0_IMG_0455.jpeg"
              alt="Meet Lucy"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#1F1812] to-transparent" />
            <div className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.2em] font-bold text-[#D4A574]">// Coach</div>
          </div>
          {/* Polaroid 1 — family selfie (bottom-right) */}
          <div className="hidden sm:block absolute -bottom-8 -right-6 lg:-right-10 w-44 lg:w-56 bg-[#F5EBE0] p-2.5 pb-10 shadow-2xl rotate-3 hover:rotate-0 transition-transform duration-300 z-10" data-testid="family-polaroid">
            <img
              src="https://customer-assets.emergentagent.com/job_fit-class-hub-2/artifacts/eifg1bdj_IMG_9636.jpeg"
              alt="Lucy and her 4 kids"
              className="w-full aspect-square object-cover"
            />
            <p className="absolute bottom-2 left-0 right-0 text-center text-[10px] uppercase tracking-[0.15em] font-bold text-[#1F1812]">Me + the chaos crew</p>
          </div>
          {/* Polaroid 2 — the 4 kids (top-left) */}
          <div className="hidden sm:block absolute -top-6 -left-6 lg:-left-10 w-40 lg:w-48 bg-[#F5EBE0] p-2.5 pb-9 shadow-2xl -rotate-6 hover:rotate-0 transition-transform duration-300 z-10" data-testid="kids-polaroid">
            <img
              src="https://customer-assets.emergentagent.com/job_fit-class-hub-2/artifacts/xq108b91_image.jpeg"
              alt="Lucy's four children"
              className="w-full aspect-square object-cover"
            />
            <p className="absolute bottom-2 left-0 right-0 text-center text-[10px] uppercase tracking-[0.15em] font-bold text-[#1F1812]">My 4 reasons</p>
          </div>
          {/* Mobile polaroids stack below */}
          <div className="sm:hidden mt-6 grid grid-cols-2 gap-4 px-4">
            <div className="bg-[#F5EBE0] p-2 pb-7 shadow-xl relative" data-testid="kids-polaroid-mobile">
              <img
                src="https://customer-assets.emergentagent.com/job_fit-class-hub-2/artifacts/xq108b91_image.jpeg"
                alt="Lucy's four children"
                className="w-full aspect-square object-cover"
              />
              <p className="absolute bottom-1.5 left-0 right-0 text-center text-[9px] uppercase tracking-[0.15em] font-bold text-[#1F1812]">My 4 reasons</p>
            </div>
            <div className="bg-[#F5EBE0] p-2 pb-7 shadow-xl relative" data-testid="family-polaroid-mobile">
              <img
                src="https://customer-assets.emergentagent.com/job_fit-class-hub-2/artifacts/eifg1bdj_IMG_9636.jpeg"
                alt="Lucy and her 4 kids"
                className="w-full aspect-square object-cover"
              />
              <p className="absolute bottom-1.5 left-0 right-0 text-center text-[9px] uppercase tracking-[0.15em] font-bold text-[#1F1812]">Me + the crew</p>
            </div>
          </div>
        </div>
        <div>
          <span className="label-overline">// Meet your coach</span>
          <h2 className="font-display text-5xl sm:text-6xl tracking-widest leading-none mt-3">I'm Lucy.</h2>
          <p className="text-zinc-300 mt-6 leading-relaxed">
            I'm a mum with 4 school-aged children, and I know how hard it is to find the time to get to the gym — or to even prioritise yourself for 30 minutes.
          </p>
          <p className="text-zinc-300 mt-4 leading-relaxed">
            I built this app to make it a tiny bit easier. There are short sessions you can follow, and you can also stack classes together to build a longer workout when you have the time.
          </p>
          <p className="text-zinc-400 mt-4 leading-relaxed">
            New classes are added regularly, so you'll always have something fresh to do.
          </p>
          <div className="mt-8 flex gap-3">
            <Link to="/classes" className="kinetic-btn" data-testid="meet-lucy-classes-btn">Browse Classes</Link>
            <Link to="/subscribe" className="label-overline border border-white/20 px-5 py-4 hover:border-[#D4A574] hover:text-[#D4A574]" data-testid="meet-lucy-sub-btn">All-Access</Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-8" data-testid="features-section">
        {[
          { icon: Fire, title: "Elite Instruction", text: "Every class is structured, timed, and cued for maximum output." },
          { icon: Barbell, title: "Progressive Overload", text: "Strength, cardio, recovery — a system not a random playlist." },
          { icon: Lock, title: "Yours Forever", text: "Buy a class once and it stays in your library. Or unlock all with a sub." },
        ].map((f, i) => (
          <div key={i} className="border border-white/10 p-8 hover:border-[#D4A574] transition-colors" data-testid={`feature-${i}`}>
            <f.icon size={32} weight="duotone" className="text-[#D4A574]" />
            <h3 className="font-display text-2xl tracking-wider mt-5">{f.title}</h3>
            <p className="text-zinc-400 mt-3 text-sm">{f.text}</p>
          </div>
        ))}
      </section>

      {/* Featured classes */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="featured-classes">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="label-overline">// Featured</span>
            <h2 className="font-display text-4xl sm:text-5xl tracking-wider leading-none mt-3">Latest drops</h2>
          </div>
          <Link to="/classes" className="label-overline hover:text-[#D4A574] flex items-center gap-2">
            All Classes <ArrowRight size={14} />
          </Link>
        </div>
        {classes.length === 0 ? (
          <div className="border border-dashed border-white/10 p-16 text-center text-zinc-500">
            <Lightning size={32} className="mx-auto mb-3 text-[#D4A574]" />
            <p className="label-overline">No classes yet — admin, start uploading.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {classes.map((c) => <ClassCard key={c.id} cls={c} />)}
          </div>
        )}
      </section>

      {/* Top tips */}
      <section className="max-w-5xl mx-auto px-6 py-24" data-testid="top-tips-section">
        <div className="text-center mb-16">
          <span className="label-overline">// Train smart</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-widest leading-[1.05] mt-4">
            Top tips for long-lasting<br />strength, shape &amp; muscle
          </h2>
          <p className="text-zinc-300 mt-6 max-w-2xl mx-auto">The five things I tell every woman who asks me how to get stronger and stay strong.</p>
        </div>

        <div className="space-y-14">
          {[
            { n: "01", body: "You don't need hours of training to get results — as long as you challenge yourself and actually work hard for the time you've got." },
            { n: "02", body: "Being consistent will create success. Even if you only manage 2 sessions a week, doing it consistently is 10× better than the all-or-nothing approach." },
            { n: "03", body: "Increase your protein intake. Most women under-eat protein. When we resistance train our bodies need protein to repair and build lean mass. Aim for around 2g of protein per kg of your desired body weight — so a woman who weighs (or wants to weigh) 65kg needs at least 130g a day. My general aim is over 100g of protein every day." },
            { n: "04", body: "Comparison is the thief of joy. Track your own progress and be proud of what you achieve." },
            { n: "05", body: "Goal setting. Have you got a reason to improve yourself? To get stronger? To stay healthy and keep muscle as you get older? Or for how you want to look? Work out your goal and keep it in mind. You can do it. Let's go 💪🏻" },
          ].map((tip, i) => (
            <React.Fragment key={tip.n}>
              <div className="grid grid-cols-[70px_1fr] sm:grid-cols-[140px_1fr] gap-5 sm:gap-10 items-start border-l-2 border-[#D4A574] pl-5 sm:pl-8" data-testid={`tip-${tip.n}`}>
                <span className="font-display text-6xl sm:text-8xl text-[#D4A574] leading-none">{tip.n}</span>
                <p className="text-zinc-100 leading-relaxed text-base sm:text-lg pt-1 sm:pt-3">{tip.body}</p>
              </div>
              {/* Photo break after tip 02 */}
              {i === 1 && (
                <div className="relative h-64 sm:h-80 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_fit-class-hub-2/artifacts/3it8w40f_IMG_4398.png"
                    alt="Lucy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1F1812]/60 to-transparent" />
                </div>
              )}
              {/* Photo break after tip 04 */}
              {i === 3 && (
                <div className="relative h-64 sm:h-80 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_fit-class-hub-2/artifacts/7cnh91hh_IMG_4405.png"
                    alt="Lucy training"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1F1812]/60 to-transparent" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link to="/classes" className="kinetic-btn inline-flex items-center gap-2" data-testid="tips-cta">
            Train with me <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Training gallery */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="gallery-section">
        <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="label-overline">// In the gym</span>
            <h2 className="font-display text-4xl sm:text-5xl tracking-wider leading-none mt-3">Training reel</h2>
            <p className="text-zinc-400 mt-4 max-w-xl">Real sessions, real sweat. No filters, no fluff.</p>
          </div>
          <a href="https://instagram.com/trainwithlucy" target="_blank" rel="noopener noreferrer" className="label-overline hover:text-[#D4A574] flex items-center gap-2" data-testid="gallery-ig-link">
            More on Instagram <ArrowRight size={14} />
          </a>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
          {[
            { src: "tmr9cmkq_IMG_6066.png", pos: "center" },
            { src: "ke6nzplx_IMG_6059.png", pos: "center" },
            { src: "vh64vjm6_IMG_5159.png", pos: "center top" },
            { src: "d4ljzd09_IMG_5151.png", pos: "center" },
            { src: "pm74gkdt_IMG_5146.png", pos: "center" },
          ].map((img, i) => (
            <div key={i} className="relative overflow-hidden aspect-square group bg-[#1F1812]" data-testid={`gallery-photo-${i}`}>
              <img
                src={`https://customer-assets.emergentagent.com/job_fit-class-hub-2/artifacts/${img.src}`}
                alt="Lucy training"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                style={{ objectPosition: img.pos }}
              />
              <div className="absolute inset-0 bg-[#1F1812]/0 group-hover:bg-[#1F1812]/20 transition-colors" />
            </div>
          ))}
        </div>
      </section>

      <ReelsRow />
    </div>
  );
}
